import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useTranslation } from "react-i18next";
import { DropdownOption, RouteParams, WaypointLanguageProps } from "../../../../types/screensTypes";
import Button from "../../../../components/ui/Button";
import { saveTemporaryWaypoint, getWaypointLanguages } from "../../../../api";
import Dropdown from "../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import { useGlobalState } from "../../../../context/useGlobalState";
import stepStyles from "../createStep.module.css";
import wp from "./WaypointEdit.module.css";

const WaypointLanguage: React.FC<WaypointLanguageProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { data, loading, error } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const { wptId } = useParams<RouteParams>();
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const saveWaypoint = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !selectedContentLanguage || !wptId) return;

        const isExistingLanguage = existingLanguages.includes(selectedContentLanguage);

        if (isExistingLanguage) {
            navigateToNext(wptId, selectedContentLanguage);
        } else {
            const tempWpt = await saveTemporaryWaypoint({
                token,
                trackId,
                internalId: wptId,
                waypointId: wptId,
                language: selectedContentLanguage,
            });
            if (tempWpt && tempWpt.temporaryWaypoint) {
                const id = tempWpt.temporaryWaypoint.internalId;
                navigateToNext(id, selectedContentLanguage);
            }
        }
    };

    const getExistingLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !wptId) return;
        const languages = await getWaypointLanguages({ token, waypointId: wptId });

        if (languages && languages.length === 0) {
            setExistingLanguages([]);
        } else {
            const languagesArray: string[] = languages.map((lang: { id: string }) => lang.id);
            setExistingLanguages(languagesArray);
        }
    };

    useEffect(() => {
        if (wptId && wptId !== "") {
            getExistingLanguagesAPI();
        }
    }, [wptId]);

    const languagesFiltered: DropdownOption[] = [
        ...(data.languagesAI?.filter((lang: DropdownOption) => existingLanguages.includes(lang.value)) || []),
        { label: t("addANewLanguage"), value: "add-new-language" },
    ];

    const languagesUnfiltered: DropdownOption[] =
        data.languagesAI?.filter((lang: DropdownOption) => !existingLanguages.includes(lang.value)) || [];

    const handleLanguageChange = (selectedOption: string): void => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true);
            setSelectedContentLanguage(null);
        } else {
            setShowAddLanguageDropdown(false);
            setSelectedContentLanguage(selectedOption);
        }
    };

    return (
        <div className={`${stepStyles.editScreen} ${wp.screen}`}>
            <TitleSubtitle title={t("selectLanguageOfYourWaypoint")} subtitle={""} layout="edit" />
            <p className={wp.langLabel}>{t("existingLanguages")}</p>
            <div className={wp.langField}>
                <Dropdown
                    label=""
                    options={languagesFiltered}
                    onChange={handleLanguageChange}
                    loading={loading}
                    error={error ? true : false}
                    placeholder={t("language")}
                    value={selectedContentLanguage}
                    fullWidth
                />
            </div>
            {showAddLanguageDropdown && (
                <>
                    <p className={wp.langLabel}>{t("selectANewLanguage")}</p>
                    <div className={wp.langField}>
                        <Dropdown
                            label=""
                            options={languagesUnfiltered}
                            onChange={setSelectedContentLanguage}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("language")}
                            value={selectedContentLanguage}
                            fullWidth
                        />
                    </div>
                </>
            )}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                    label={t("continue")}
                    onClick={saveWaypoint}
                />
            </div>
        </div>
    );
};

export default WaypointLanguage;
